import React from 'react';
import PropTypes from 'prop-types';
import { ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import reviewConstants from '../../../../../constants/review.constants';
import { cn } from '../../../../../lib/utils';
import ReviewsTableRow from './ReviewsTableRow';
import ReviewsTableRowSkeleton from './ReviewsTableRowSkeleton';

const TABLE_COLUMNS = [
  { id: 'avatar', name: '', width: '50px', sortable: false, align: 'center' },
  { id: 'email', name: 'Email', width: '180px', sortable: true, align: 'left' },
  { id: 'department', name: 'Department', width: '120px', sortable: true, align: 'left' },
  { id: 'reviewGoal', name: 'Review Goal', width: '100px', sortable: true, align: 'left' },
  { id: 'reviewRecordLinks', name: 'Record', width: '80px', sortable: false, align: 'left' },
  { id: 'newGoal', name: 'New Goal', width: '100px', sortable: true, align: 'left' },
  { id: 'status', name: 'Status', width: '100px', sortable: true, align: 'center' },
  { id: 'confirmed', name: 'Confirmed', width: '80px', sortable: true, align: 'center' },
  { id: 'declined', name: 'Declined', width: '80px', sortable: true, align: 'center' },
  { id: 'progress', name: 'Progress', width: '140px', sortable: true, align: 'center' },
  { id: 'updatedAt', name: 'Review Date', width: '110px', sortable: true, align: 'left' },
  { id: 'expand', name: '', width: '50px', sortable: false, align: 'center' },
];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

function ReviewsTable({
  reviews,
  isLoading,
  totalCount,
  currentPage,
  rowsPerPage,
  sortConfig,
  onSort,
  onPageChange,
  onRowsPerPageChange,
}) {
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startItem = currentPage * rowsPerPage + 1;
  const endItem = Math.min((currentPage + 1) * rowsPerPage, totalCount);

  // Filter out reverted reviews from the main list
  const filteredReviews = reviews.filter(
    (review) => review.status !== reviewConstants.REVIEW_STATUSES.REVERTED,
  );

  return (
    <div className="w-full">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              {TABLE_COLUMNS.map((column) => (
                <th
                  key={column.id}
                  className={cn(
                    'px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider',
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right',
                    column.align === 'left' && 'text-left',
                  )}
                  style={{ width: column.width, minWidth: column.width }}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                      onClick={() => onSort(column.id)}
                    >
                      {column.name}
                      <ArrowUpDown
                        className={cn('h-3 w-3', sortConfig.key === column.id && 'text-primary')}
                      />
                    </button>
                  ) : (
                    column.name
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 5 }, (_, index) => <ReviewsTableRowSkeleton key={index} />)
              : filteredReviews.map((review) => (
                  <ReviewsTableRow key={review._id} review={review} allReviews={reviews} />
                ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {!isLoading && filteredReviews.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          No reviews found. Try adjusting your filters.
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows per page:</span>
            <Select
              value={String(rowsPerPage)}
              displayValue={String(rowsPerPage)}
              onValueChange={(value) => onRowsPerPageChange(Number(value))}
            >
              <SelectTrigger className="w-16 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROWS_PER_PAGE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span className="text-sm text-muted-foreground">
            {totalCount > 0 ? `${startItem}-${endItem} of ${totalCount}` : '0 results'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground px-2">
            Page {currentPage + 1} of {totalPages || 1}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

ReviewsTable.propTypes = {
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      department: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      updatedAt: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  totalCount: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  sortConfig: PropTypes.shape({
    key: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }).isRequired,
  onSort: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
};

export default ReviewsTable;
